import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/recommendationEngine';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

const POLL_INTERVAL = 5000;
const MAX_POLLS = 12;
const ACTIVE_PAYMENT_KEY = 'a1looks_active_payment';
const CHECKOUT_REQUEST_KEY = 'a1looks_checkout_request';

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { items, dispatch } = useCart();
  const { user } = useAuth();
  const orderId = searchParams.get('order_id');
  const cartFinalized = useRef(false);
  const pollCount = useRef(0);
  const cartItems = useRef(items);

  const [status, setStatus] = useState('checking'); // checking | paid | failed | error
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    cartItems.current = items;
  }, [items]);

  useEffect(() => {
    if (!orderId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    pollCount.current = 0;

    const checkStatus = async () => {
      try {
        const { data } = await api.get(`/payment/status/${orderId}/`);
        if (cancelled) return;

        setOrderData(data);

        const cf = (data.cashfree_order_status || '').toUpperCase();
        const local = (data.payment_status || '').toLowerCase();

        if (cf === 'PAID' || local === 'paid') {
          if (!cartFinalized.current) {
            try {
              const activePayment = JSON.parse(
                localStorage.getItem(ACTIVE_PAYMENT_KEY) || 'null'
              );
              if (activePayment?.order_id === orderId) {
                cartItems.current.forEach(item => {
                  const categorySlug = item.product.category_slug || item.product.category?.slug;
                  if (categorySlug) trackEvent(categorySlug, 'purchase');
                });
                dispatch({ type: 'CLEAR_CART' });
                localStorage.removeItem(ACTIVE_PAYMENT_KEY);
                localStorage.removeItem(CHECKOUT_REQUEST_KEY);
              }
            } catch {}
            cartFinalized.current = true;
          }
          setStatus(data.requires_manual_review ? 'review' : 'paid');
          return; // done
        }
        if (cf === 'EXPIRED' || cf === 'TERMINATED' || local === 'failed') {
          try {
            const activePayment = JSON.parse(
              localStorage.getItem(ACTIVE_PAYMENT_KEY) || 'null'
            );
            if (activePayment?.order_id === orderId) {
              localStorage.removeItem(ACTIVE_PAYMENT_KEY);
              localStorage.removeItem(CHECKOUT_REQUEST_KEY);
            }
          } catch {}
          setStatus('failed');
          return;
        }

        // Still pending — poll again unless we've hit the limit
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setStatus('pending');
          return;
        }
        setTimeout(checkStatus, POLL_INTERVAL);
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    checkStatus();
    return () => { cancelled = true; };
  }, [dispatch, orderId]);

  if (status === 'checking') {
    return (
      <div style={{ textAlign: 'center', padding: '100px 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: '#2C2C2C', marginBottom: 8 }}>
          Verifying Payment…
        </h2>
        <p style={{ color: '#888', fontSize: 15 }}>Please wait while we confirm your payment.</p>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', maxWidth: 520, margin: '0 auto' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: '#2e7d32', marginBottom: 12 }}>
          Payment Successful!
        </h2>
        {orderData && (
          <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '20px', marginBottom: 24, textAlign: 'left', border: '1px solid #e0e0e0' }}>
            <p style={{ fontSize: 14, color: '#555', marginBottom: 6 }}>
              <strong>Order ID:</strong> {orderData.order_id}
            </p>
            <p style={{ fontSize: 14, color: '#555', marginBottom: 6 }}>
              <strong>Amount Paid:</strong> ₹{parseFloat(orderData.total).toLocaleString('en-IN')}
            </p>
          </div>
        )}
        <p style={{ color: '#555', fontSize: 15, marginBottom: 32 }}>
          Thank you for your order! We have confirmed your payment and will send order confirmation and shipping updates to the email used at checkout.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {user && (
            <button
              onClick={() => navigate('/my-orders')}
              style={{ background: C.primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              View My Orders
            </button>
          )}
          <button
            onClick={() => navigate('/')}
            style={{ background: '#fff', color: C.primary, border: `2px solid ${C.primary}`, padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>❌</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: '#e53935', marginBottom: 12 }}>
          Payment Failed
        </h2>
        <p style={{ color: '#555', fontSize: 15, marginBottom: 32 }}>
          Your payment could not be completed. No amount has been charged. Please try again or use a different payment method.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/checkout')}
            style={{ background: C.primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/cart')}
            style={{ background: '#fff', color: C.primary, border: `2px solid ${C.primary}`, padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Back to Cart
          </button>
        </div>
      </div>
    );
  }

  if (status === 'review') {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', maxWidth: 520, margin: '0 auto' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🛡️</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, color: C.gold, marginBottom: 12 }}>
          Payment Received
        </h2>
        <p style={{ color: '#555', fontSize: 15, marginBottom: 16 }}>
          Your payment was confirmed, but this order needs a quick stock review before fulfilment.
        </p>
        <p style={{ color: '#888', fontSize: 13, marginBottom: 28 }}>
          Order reference: {orderData?.order_id}
        </p>
        <button
          onClick={() => navigate('/')}
          style={{ background: C.primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🕐</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: C.gold, marginBottom: 12 }}>
          Payment Pending
        </h2>
        <p style={{ color: '#555', fontSize: 15, marginBottom: 16 }}>
          Your payment is still being processed. This can take a few minutes.
        </p>
        {orderData && (
          <p style={{ fontSize: 13, color: '#888', marginBottom: 32 }}>Order reference: {orderData.order_id}</p>
        )}
        <p style={{ fontSize: 13, color: '#aaa', marginBottom: 24 }}>
          If payment was deducted, your order will be confirmed within 24 hours. Contact us if you face any issues.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => window.location.reload()}
            style={{ background: C.primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Refresh Status
          </button>
          <button
            onClick={() => navigate('/')}
            style={{ background: '#fff', color: C.primary, border: `2px solid ${C.primary}`, padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // error state
  return (
    <div style={{ textAlign: 'center', padding: '80px 24px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: '#2C2C2C', marginBottom: 12 }}>
        Something went wrong
      </h2>
      <p style={{ color: '#888', fontSize: 15, marginBottom: 28 }}>
        We couldn't verify your payment status. Please check your email or contact us at rohini@a1looks.com.
      </p>
      <button
        onClick={() => navigate('/')}
        style={{ background: C.primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        Go Home
      </button>
    </div>
  );
}
